import { ReactElement, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Button,
  paletteHelper,
  spacingHelper,
  UserFilledLg,
} from '@construct-kit/core';
import { AccountMenu } from './AccountMenu';

type AccountProps = {
  trackClickThrough: (element: string) => void;
};

const StyledLink = styled(Button)`
  padding: 0.375rem ${spacingHelper('extraSmall')};
  color: ${paletteHelper('textPrimary')};
  font-size: 0.875rem;
  line-height: 1.25rem;
  min-height: 0;

  ${({ theme }) => theme.layoutBreakpoints.medium} {
    padding: 0.375rem ${spacingHelper('medium')};
  }

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    font-size: ${spacingHelper('medium')};
    line-height: ${spacingHelper('large')};
    padding: ${spacingHelper('extraSmall')};
  }

  svg {
    height: 1.25rem;
    width: 1.25rem;

    ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
      height: ${spacingHelper('large')};
      width: ${spacingHelper('large')};
    }
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

export const Account = ({ trackClickThrough }: AccountProps): ReactElement => {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: Event): void => {
      const node = ref.current;
      const { target } = event;

      if (node && target instanceof Element && !node.contains(target)) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <Wrapper ref={ref}>
      <StyledLink
        onClick={() => setAccountMenuOpen(true)}
        variant="naked"
        sizeVariant="medium"
        icon={<UserFilledLg />}
      >
        Account
      </StyledLink>
      {accountMenuOpen && (
        <AccountMenu
          setAccountMenuOpen={setAccountMenuOpen}
          trackClickThrough={trackClickThrough}
        />
      )}
    </Wrapper>
  );
};
